import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Card, Icon, Layout, SearchBox, Justify, Segment } from 'tea-component';
import Tabs from '@intelligence/components/NewIconTabs';
import useData, { DEFAULT_ACTIVE_ID } from './hooks';
import '@/pages/intelligence/components/LazyReport/index.scss';
import InsightsReportCard from '../../components/ReportCard/InsightsReportCard';
import { useIntl, getLocale } from 'umi';
import { debounce } from 'lodash-es';
import { buriedPointPage } from '@/pages/home/utils/buriedPoint';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import moment from 'moment';
import Loading from '@/pages/intelligence/components/Loading';
import classnames from 'classnames';
import { pageReportTypeConfig } from '@intelligence/pages/InsightsReport/_config';
import { IS_WOA } from '@home/_config/config';
import SortBySelector, {
  OrderTypeMap,
} from '@intelligence/pages/InsightsReport/components/SortBySelector';
import { sortReportsData } from '@intelligence/pages/InsightsReport/_utils';
import { normalizeLanguage, collectLanguageTabs, LANG_TAB_I18N_MAP } from '@intelligence/utils/reportLanguage';
import MarqueeBanner from '@intelligence/components/MarqueeBanner/index';

import './index.scss';

const { Content } = Layout;

const NEW_MARK_IDS_MAPPING_KEY = 'ReportsInsightsReports';

export interface ReportProps {
  title?: any;
  subTitle?: string;
  segmentList?: any[];
  reportType?: 'gameAnalysis' | 'insightsReports' | 'gameResearchReport';
  isEnType?: boolean;
  segmentValue?: string;
}

export default function InsightsReport(props: ReportProps) {
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const { title, subTitle, reportType } = props;

  const scrollBody = useRef<HTMLDivElement>(null);
  const [segmentFixed, setSegmentFixed] = useState(false);
  const [orderType, setOrderType] = useState<any>(OrderTypeMap.time);
  const isEnType = useMemo(() => {
    if (segmentValue === 'all' || !segmentValue) {
      return locale === 'en-US';
    }
    return normalizeLanguage(segmentValue).toLowerCase() !== 'chinese';
  }, [segmentValue, locale]);

  // 当前是否点赞数变动
  const isLikeViewChangedRef = useRef<boolean>();

  // 记录排序顺序（点赞浏览发生变化，避免列表顺序变化，用原有顺序重置）
  const sortOrderRef = useRef<string[]>();

  const {
    activeId,
    // setActiveId,
    onSearch,
    showSearchRecent,
    showSearchRecentFixed,
    setShowSearchRecent,
    setShowSearchRecentFixed,
    searchRecentProper,
    searchRecentProperFixed,
    handleSearchRecentClick,
    deleteSearchRecent,
    cacheRecentSearch,
    dataLoading,
    dataPartial,
    lang,
    search,
    onClear,
    shareBack,
    setSearch,
    lazyScrollRef,
    data,
    loading,
    loadingMore,
    noMore,
    segmentList = [],
    handleTabChange,
    mutate,
    segmentValue,
    setSegmentValue,
  } = useData({ ...props });

  // console.log('insightsReport',props);

  const dataListCur = useMemo(() => {
    if (data && Array.isArray(data?.list) && data?.list?.length > 0) {
      return data?.list;
    }
    return [];
  }, [data, locale]);

  // 监听页面滚动
  useEffect(() => {
    if (scrollBody.current) {
      const handleScroll = debounce((e: any) => {
        const { scrollTop } = e.srcElement;

        // 滚动判断距离 和定位条位置相关，开发调整
        if (reportType === 'gameResearchReport') {
          if (scrollTop > 260) {
            setSegmentFixed(true);
            setShowSearchRecent(false);
          } else {
            setSegmentFixed(false);
            setShowSearchRecent(false);
            setShowSearchRecentFixed(false);
          }
        } else {
          setSegmentFixed(false);
          setShowSearchRecent(false);
          setShowSearchRecentFixed(false);
        }
      }, 100);
      scrollBody.current?.addEventListener('scroll', handleScroll, false);
      return () => scrollBody.current?.removeEventListener('scroll', handleScroll, false);
    }
  }, [scrollBody]);

  const handleTabChangeActive = (tab: any) => {
    handleTabChange(tab);
    if (dbiPageLogMes?.[tab?.pagePoint]) {
      buriedPointPage(dbiPageLogMes?.[tab?.pagePoint]);
    }
  };

  const langSegmentCur = useMemo(() => {
    if (segmentValue === 'englishReport') {
      return 'en';
    }
    if (segmentValue === 'chineseReport') {
      return 'cn';
    }
    return '';
  }, [segmentValue]);

  /**
   * 获得当前展示语言：始终跟随系统 locale，
   * 不受语言 Tab 影响。
   * 语言 Tab 仅用于过滤报告以及决定 PDF 文件的语言版本。
   */
  const displayLang = lang;

  // 对报告重新排序
  const newDataList = useMemo(() => {
    if (data && Array.isArray(data?.list) && data?.list?.length > 0) {
      const searchValue = typeof search === 'string' ? search?.trim() : '';
      const dataArr = dataListCur.filter((item: any) => {
        if (activeId === DEFAULT_ACTIVE_ID) return true;
        return item.segment_en === activeId;
      });

      // 如果是点赞浏览变化，排序按照原有排序不做重新排序
      if (isLikeViewChangedRef.current) {
        isLikeViewChangedRef.current = false;
        dataArr?.sort((a, b) => {
          const aIdIndex = sortOrderRef.current?.indexOf(a?.id) || 0;
          const bIdIndex = sortOrderRef.current?.indexOf(b?.id) || 0;
          return aIdIndex - bIdIndex;
        });
        return dataArr;
      }

      // 若有搜索条件，则优先按搜索结果排序
      // 搜索结果的排序按照标题+tag+简介包含单词最多的报告进行降序
      if (searchValue !== '') {
        dataArr.sort((a: any, b: any) => {
          if (a.searchTotalNum === b.searchTotalNum) {
            // 总数值相等时，比较searchTitleNum标题匹配数量
            // 如果数量一致按照标题>tag>简介的顺序
            if (a.searchTitleNum !== b.searchTitleNum) {
              return b.searchTitleNum - a.searchTitleNum;
            }
            if (a.searchTagNum !== b.searchTagNum) {
              // 总数值、标题相等时，比较searchTagNum标签匹配数量
              return b.searchTagNum - a.searchTagNum;
            }
            if (a.searchTextNum !== b.searchTextNum) {
              // 总数值、标题、描述相等时，比较searchTextNum描述匹配数量
              return b.searchTextNum - b.searchTextNum;
            }
            // 如果还是一致按照上传时间降序
            // return moment(b.update_time || b.time) - moment(a.update_time || a.time);
            return (
              moment(b.create_time || b.time).valueOf() - moment(a.create_time || a.time).valueOf()
            );
          }
          return b.searchTotalNum - a.searchTotalNum;
        });
      } else {
        // 若没有搜索，则按排序类型来排，默认按点赞数量排序
        sortReportsData(dataArr, orderType);
      }

      // 记录当前列表 id 顺序
      sortOrderRef.current = dataArr?.map((item) => item.id);

      return dataArr;
    }
    return [];
  }, [dataListCur, orderType, segmentValue, activeId]);

  const onClickSearchRecent = (item: any) => {
    setShowSearchRecent(false);
    setShowSearchRecentFixed(false);
    handleSearchRecentClick(item);
  };

  const segmentListCur = useMemo<string>(() => {
    if (Array.isArray(segmentList) && segmentList?.length > 0) {
      return segmentList?.map((item: any) => {
        const strText =
          locale === 'en-US' && item.segment_en !== '' ? item.segment_en : item.segment;
        return {
          id: item.segment_en,
          label: strText,
        };
      });
    }
    return segmentList;
  }, [segmentList, locale]);

  const languageSegmentListCur = useMemo(() => {
    const allTab = [{ text: formatMessage({ id: 'dbi_reports_all' }), value: 'all' }];
    if (!dataPartial?.length) return allTab;
    return [...allTab, ...collectLanguageTabs(dataPartial, 'filePosition', (lang) => {
      const key = LANG_TAB_I18N_MAP[lang];
      return key ? formatMessage({ id: key }) : `${lang} Report`;
    })];
  }, [dataPartial, locale]);

  interface IHandleCountsDataChangeProps {
    resourceId: string;
    field: 'report_like' | 'watched_cnt';
    counts?: number;
  }

  // 点赞数/浏览数变化更新数据并排序
  const handleCountsDataChange = ({ resourceId, field, counts }: IHandleCountsDataChangeProps) => {
    // 点赞数/浏览数变化标识
    isLikeViewChangedRef.current = true;

    // 当前展示数据对应数据处理
    const newData: any = {
      ...data,
      list: data?.list?.map((item) => {
        if (resourceId === item?.resource_id) {
          const newCounts = Number(counts) > 0 ? counts : item?.[field] + 1;
          return { ...item, [field]: newCounts };
        }
        return item;
      }),
    };

    // 更新原始数据点赞/浏览数
    const currentReport: any = dataPartial?.find((item: any) => resourceId === item?.resource_id);
    const newCounts = Number(counts) > 0 ? counts : (currentReport?.[field] || 0) + 1;
    currentReport && (currentReport[field] = newCounts);

    mutate({ ...newData });
  };

  // 点赞量数据处理
  const handleLikeBtnClick = (resourceId: string, likeCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: likeCounts, field: 'report_like' });
  };

  // 浏览量数据处理
  const handleViewsChange = (resourceId: string, viewCounts?: number) => {
    handleCountsDataChange({ resourceId, counts: viewCounts, field: 'watched_cnt' });
  };

  return (
    <Content className="dbi-insightsreport" ref={scrollBody}>
      <Content.Header
        className={classnames([
          'dbi-header contentHead-normal dbi-header-des',
          segmentListCur?.length ? 'dbi-insightsreport-header' : 'dbi-report-header',
        ])}
        title={title}
      >
        <div className="header-des">
          <span className="header-des-text">{subTitle}</span>
        </div>
        <div className="dbi-insightsreport-searchbox" ref={searchRecentProper}>
          <SearchBox
            value={search == null ? '' : search}
            className="edit-search-box is-search-box"
            onChange={(value) => {
              // console.log('onChangeonChange',value);
              setSearch(value.trim());
            }}
            onSearch={(word) => {
              // console.log('onSearchonSearch',word);
              onSearch(word?.trim());
              setShowSearchRecent(false);
            }}
            onFocus={() => {
              setShowSearchRecent(true);
            }}
            // onClear={() => { onClear() }}
            placeholder={formatMessage({ id: 'dbi_insights_report_search_or_tag' })}
          />
          {showSearchRecent && (
            <Card className="dbi-insightsreport-recent-search-card">
              <Card.Body>
                <div className="dbi-insightsreport-recent-search-recent">
                  {!cacheRecentSearch.length
                    ? `${formatMessage({ id: 'dbi_insights_report_search_no_data' })}`
                    : `${formatMessage({ id: 'dbi_insights_report_search_recent' })}`}
                </div>
                <div className="dbi-insightsreport-recent-search-box">
                  {cacheRecentSearch?.map((item, index) => (
                    <div className="dbi-insightsreport-recent-search-item" key={index}>
                      <div className="search-text" onClick={() => onClickSearchRecent(item)}>
                        {item}
                      </div>
                      <Icon
                        type="dismiss"
                        aria-label="dismiss"
                        onClick={(e) => deleteSearchRecent(e, index, item)}
                      />
                    </div>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}
        </div>
        {segmentListCur?.length > 0 ? (
          <div className="header-tab dbi-insightsreport-tab">
            <Tabs
              ceiling
              newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
              tabs={segmentListCur}
              animated={false}
              activeId={activeId}
              onActive={(tab) => {
                handleTabChangeActive(tab);
              }}
            />
          </div>
        ) : null}
      </Content.Header>

      <Content.Body
        full
        ref={lazyScrollRef}
        className="dbi-lazyreport-full dbi-insightsreport-body-full"
      >
        <MarqueeBanner />
        {/* 吸顶内容 */}
        <div
          className={`dbi-insightsreport-header-fixed header-segment ${
            segmentFixed ? 'visible' : 'hide'
          }`}
        >
          {segmentFixed && (
            <>
              <div className="dbi-insightsreport-searchbox" ref={searchRecentProperFixed}>
                <SearchBox
                  value={search}
                  className="edit-search-box is-search-box"
                  onChange={(value: any) => {
                    setSearch(value.trim());
                  }}
                  onSearch={(word: any) => {
                    onSearch(word.trim());
                  }}
                  onFocus={() => {
                    setShowSearchRecentFixed(true);
                  }}
                  onClear={() => {
                    onClear();
                  }}
                  placeholder={formatMessage({ id: 'dbi_insights_report_search' })}
                />
                {showSearchRecentFixed && (
                  <Card className="dbi-insightsreport-recent-search-card">
                    <Card.Body>
                      <div className="dbi-insightsreport-recent-search-recent">
                        {!cacheRecentSearch.length
                          ? `${formatMessage({ id: 'dbi_insights_report_search_no_data' })}`
                          : `${formatMessage({ id: 'dbi_insights_report_search_recent' })}`}
                      </div>
                      <div className="dbi-insightsreport-recent-search-box">
                        {cacheRecentSearch?.map((item, index) => (
                          <div className="dbi-insightsreport-recent-search-item" key={index}>
                            <div className="search-text" onClick={() => onClickSearchRecent(item)}>
                              {item}
                            </div>
                            <Icon
                              type="dismiss"
                              aria-label="dismiss"
                              onClick={(e: any) => deleteSearchRecent(e, index, item)}
                            />
                          </div>
                        ))}
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </div>
              {segmentListCur?.length > 0 ? (
                <div className="header-tab dbi-insightsreport-tab">
                  <Tabs
                    ceiling
                    newMarkIdsMappingKey={NEW_MARK_IDS_MAPPING_KEY}
                    tabs={segmentListCur}
                    animated={false}
                    activeId={activeId}
                    onActive={(tab) => {
                      handleTabChangeActive(tab);
                    }}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>

        <div className="dbi-imur-filterheader">
          <Justify
            className="dbi-imurreports-justify"
            left={
              segmentList?.length > 0 && (
                <Segment
                  className="segment-light"
                  value={segmentValue}
                  onChange={(value: any) => setSegmentValue(value)}
                  options={languageSegmentListCur}
                />
              )
            }
            right={
              <SortBySelector
                defaultValue={orderType}
                onChange={(value: string) => {
                  setOrderType(value);
                }}
              />
            }
          />
        </div>

        {loading ? (
          <div style={{ height: 'calc(100vh - 300px)', color: '#262832' }}>
            <Loading content={formatMessage({ id: 'common_loading' })} />
          </div>
        ) : (
          // <div className='dbi-lazyreport-loading'>
          //   <Icon size="l" type="loading" />
          //   &nbsp;&nbsp;&nbsp;{formatMessage({ id: 'dbi_reports_loading' })}
          // </div>
          // : newDataList?.length == 0 ? (
          //   <div style={{ fontSize: 14, height: 350 }}>
          //     <NoData content={nodataMsgCur} />
          //   </div>
          // )
          <div>
            {newDataList?.map((item) => {
              return (
                <InsightsReportCard
                  key={item.resource_id}
                  data={item}
                  // tabPointInfo={tabPointInfo}
                  systemId={'intelligence_insights'}
                  lang={displayLang}
                  reportType={reportType}
                  isNew={
                    // !!moment(item?.update_time || item?.time).isAfter(moment().subtract(1, 'month'))
                    !!moment(item?.create_time || item?.time).isAfter(moment().subtract(1, 'month'))
                  }
                  needPopover
                  shareBack={shareBack}
                  // isShowCreatorBox={false}
                  isEnType={isEnType}
                  reportTypeConfig={pageReportTypeConfig.gameResearchReport}
                  // 除 woa 环境外，展示邮箱信息
                  showContactInfo={!IS_WOA}
                  onChange={handleLikeBtnClick}
                  onViewsChange={handleViewsChange}
                  reportLangSegment={segmentValue}
                />
              );
            })}
          </div>
        )}
        {loadingMore && (
          <div className="dbi-lazyreport-loading dbi-lazyreport-end">
            <Icon size="l" type="loading" />
            &nbsp;&nbsp;&nbsp;{formatMessage({ id: 'dbi_reports_loading_more' })}
          </div>
        )}

        {dataPartial.length <= 0 && noMore && !loading && !dataLoading && (
          <div className="dbi-reportcard-nodata dbi-insightsreport-nodata">
            {formatMessage({ id: 'dbi_reports_search_na_data' })}
          </div>
        )}
      </Content.Body>
    </Content>
  );
}
